import {
  SignUpFormWrapper,
  FormWrapper,
  FormGrid,
  FormField,
  SeparatorWrapper,
  PasswordWrapper,
  LoginLink,
  FooterLinks,
  RegisterButton,
  NavigateParagraph,
} from "./Styles";
import { Button } from "../../../../Components/Button";
import { Input } from "../../../../Components/Input";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaLinkedin } from "react-icons/fa";
import { HiOutlineUser, HiOutlineBuildingOffice, HiOutlineLockClosed, HiArrowRight } from "react-icons/hi2";
import { HiOutlineMail } from "react-icons/hi";
import { useAuth } from "../../../../Common/Hooks/useAuth";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { registerState } from "../../../../Common/State/Register";
import { ErrorMessage } from "../../../../Components/ErrorMessage";
import { CustomPhoneInput } from "../../../../Components/CustomPhoneInput";
import { Turnstile } from "../../../../Components/Turnstile";
import { isErr } from "../../../../Common/Utils/Result";
import { ClientUserSsoProviderType } from "../../../../Common/Types-DTOs/ClientUser";
import { eventBus } from "../../../../Common/EventBus";
import { Notification } from "../../../../Components/Notifications";
import { getFormattedErrorMessage } from "../../../../Common/Utils/errorMessages";

export const SignUpForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { preRegisterUserWithGoogle, preRegisterUserWithLinkedin, registerUser, registerUserWithSso } = useAuth();
  const [register] = useRecoilState(registerState);

  const [formData, setFormData] = useState({
    name: register.name || "",
    businessName: register.businessName || "",
    email: register.email || "",
    phone: register.phone || "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);
  useEffect(() => {
    if (register.ssoProvider && register.ssoToken) {
      setFormData(prev => ({
        ...prev,
        email: register.email || prev.email,
      }));
    }
  }, [register]);

  const validateEmail = (email: string) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateSpanishMobile = (phone: string) => {
    const isSpanishPrefix = /^\+?34/.test(phone);
    if (!isSpanishPrefix) return true;
    const spanishMobileRegex = /^\+?34[67]\d{8}$/;
    return spanishMobileRegex.test(phone);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("auth.register.errors.nameRequired");
    }

    if (!formData.businessName.trim()) {
      newErrors.businessName = t("auth.register.errors.businessNameRequired");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("auth.register.errors.emailRequired");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("auth.register.errors.emailInvalid");
    }

    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = t("auth.register.errors.phoneRequired");
    } else if (!validateSpanishMobile(formData.phone)) {
      newErrors.phone = t("auth.register.errors.phoneInvalid");
    }

    if (!formData.password) {
      newErrors.password = t("auth.register.errors.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("auth.register.errors.passwordTooShort");
    }

    if (!turnstileToken) {
      newErrors.turnstile = t("auth.register.errors.turnstileRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    let result;

    if (register.ssoProvider && register.ssoToken) {
      result = await registerUserWithSso({
        businessName: formData.businessName,
        clientPhone: formData.phone,
        clientName: formData.name,
        clientPassword: formData.password,
        clientSsoProvider: register.ssoProvider as ClientUserSsoProviderType,
        clientAuthToken: register.ssoToken as string,
        turnstileToken,
      });
    } else {
      result = await registerUser({
        businessName: formData.businessName,
        clientEmail: formData.email,
        clientPhone: formData.phone,
        clientName: formData.name,
        clientPassword: formData.password,
        turnstileToken,
      });
    }

    setLoading(false);

    if (isErr(result)) {
      setTurnstileToken("");
      setTurnstileKey((key) => key + 1);

      if (result.error?.fields?.clientPhone) {
        setErrors((prev) => ({ ...prev, phone: t("auth.register.errors.phoneInvalid") }));
        return;
      }

      const errorMessage = getFormattedErrorMessage(
        result.error?.errorCode,
        t("auth.register.errors.registerFailed")
      );
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", errorMessage)
      );
      return;
    }

    navigate("/chat");
  };

  const registerWithGoogle = async () => {
    preRegisterUserWithGoogle();
  };

  const registerWithLinkedIn = async () => {
    preRegisterUserWithLinkedin();
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <SignUpFormWrapper>
      <h1>{t("auth.register.title")}</h1>
      <p className="subtitle">{t("auth.register.subtitle")}</p>

      <Button onClick={registerWithGoogle}>
        <FcGoogle />
        {t("auth.register.googleButton")}
      </Button>
      <Button onClick={registerWithLinkedIn}>
        <FaLinkedin />
        {t("auth.register.linkedinButton")}
      </Button>

      <SeparatorWrapper>
        <span>{t("auth.register.orSeparator")}</span>
      </SeparatorWrapper>

      <FormWrapper onSubmit={handleSubmit}>
        <FormGrid>
          <FormField>
            <Input
              Icon={HiOutlineUser}
              placeholder={t("auth.register.namePlaceholder")}
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
            />
            {errors.name && <ErrorMessage errorMessage={errors.name} />}
          </FormField>
          <FormField>
            <Input
              Icon={HiOutlineBuildingOffice}
              placeholder={t("auth.register.businessNamePlaceholder")}
              type="text"
              value={formData.businessName}
              onChange={(e) => handleChange("businessName", e.target.value)}
              error={!!errors.businessName}
            />
            {errors.businessName && <ErrorMessage errorMessage={errors.businessName} />}
          </FormField>
        </FormGrid>

        <FormField>
          <Input
            Icon={HiOutlineMail}
            placeholder={t("auth.register.emailPlaceholder")}
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={!!errors.email}
          />
          {errors.email && <ErrorMessage errorMessage={errors.email} />}
        </FormField>

        <FormField>
          <CustomPhoneInput
            placeholder={t("auth.register.phonePlaceholder")}
            value={formData.phone}
            onChange={(phoneNumber) => handleChange("phone", phoneNumber)}
          />
          {errors.phone && <ErrorMessage errorMessage={errors.phone} />}
        </FormField>

        <FormField>
          <PasswordWrapper>
            <Input
              Icon={HiOutlineLockClosed}
              placeholder={t("auth.register.passwordPlaceholder")}
              type="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              error={!!errors.password}
            />
            {errors.password && <ErrorMessage errorMessage={errors.password} />}
            <p className="password-hint">
              {formData.password && formData.password.length < 8 ? (
                <span style={{ color: "#ef4444" }}>{t("auth.register.passwordHint")}</span>
              ) : (
                t("auth.register.passwordHint")
              )}
            </p>
          </PasswordWrapper>
        </FormField>

        <FormField>
          <Turnstile key={turnstileKey} onVerify={setTurnstileToken} />
          {errors.turnstile && <ErrorMessage errorMessage={errors.turnstile} />}
        </FormField>

        <RegisterButton type="submit" className="nextstep" isLoading={loading} disabled={loading}>
          {loading ? t("auth.register.submitButtonLoading") : (
            <>
              <span>{t("auth.register.submitButton")}</span>
              <HiArrowRight />
            </>
          )}
        </RegisterButton>
      </FormWrapper>

      <NavigateParagraph>
        {t("auth.register.hasAccount")}{" "}
        <LoginLink onClick={() => navigate("/login")}>{t("auth.register.loginLink")}</LoginLink>
      </NavigateParagraph>

      <FooterLinks>
        <a href="https://www.virtualmike.net/terms-and-conditions" target="_blank" rel="noopener noreferrer">{t("auth.register.termsLink")}</a>
        <a href="https://www.virtualmike.net/privacy-policy" target="_blank" rel="noopener noreferrer">{t("auth.register.privacyLink")}</a>
        <a href="https://www.virtualmike.net/#soporte" target="_blank" rel="noopener noreferrer">{t("auth.register.helpLink")}</a>
      </FooterLinks>
    </SignUpFormWrapper>
  );
};

